import _ from 'lodash';
import { useEffect, useState } from 'react';
import { MapRef } from 'react-map-gl';
import { useDebounce } from 'react-use';
import { useAppSelector } from '../../../app/hooks';
import { selectDuckDBStatus } from '../../duckDB/duckDBSlice';
import {
  selectAllSelectState,
  selectScanDataStatus,
} from '../../select/selectSlice';
import { LayerEnum, selectLayerVisibility } from '../mapSlice';

export type LayerExistenceDict = Record<LayerEnum, boolean>;

export const initialLayersExist: LayerExistenceDict = {
  [LayerEnum.blockNames]: false,
  [LayerEnum.blockOutlines]: false,
  [LayerEnum.markers]: false,
  [LayerEnum.heatmap]: false,
  [LayerEnum.heatmapCurrentPoint]: false,
  [LayerEnum.heatmapFadeOutHighlight]: false,
};

/**
 * Checks the existence of specified map layers.
 * @param {MapRef | null} mapRef - Reference to the map.
 * @return {LayerExistenceDict} - Dictionary indicating the existence of each map layer.
 */
const useCheckLayers = (mapRef: MapRef | null): LayerExistenceDict => {
  const selectStateAll = useAppSelector(selectAllSelectState);
  const duckDBStatus = useAppSelector(selectDuckDBStatus);
  const scanDataStatus = useAppSelector(selectScanDataStatus);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const [mapboxDataEventOccurred, setMapboxDataEventOccurred] =
    useState<number>(0);
  const [debouncedTriggerLayerCheck, setDebouncedTriggerLayerCheck] =
    useState<number>(0);

  // Initialize the state with all layers as not existing
  const [layersExist, setLayersExist] =
    useState<LayerExistenceDict>(initialLayersExist);

  // Listen for map  events to trigger layer check after data is loaded.  Other events are possible too, these are just arbitrary ones that gets the job done.
  useEffect(() => {
    mapRef?.on('sourcedataloading', (e) => {
      if (e.isSourceLoaded) {
        setMapboxDataEventOccurred((prev) => prev + 1);
      }
    });

    mapRef?.on('resize', (e) => {
      if (e) {
        setMapboxDataEventOccurred((prev) => prev + 1);
      }
    });

    mapRef?.on('load', (e) => {
      if (e) {
        setMapboxDataEventOccurred((prev) => prev + 1);
      }
    });

    mapRef?.on('zoomend', (e) => {
      if (e) {
        setMapboxDataEventOccurred((prev) => prev + 1);
      }
    });
  }, [mapRef]);

  // Debounce the triggerLayerCheck state to prevent multiple checks in quick succession.  This is necessary because the mapbox data source load event fires multiple times in quick succession.  The trigger conditions are somewhat arbitrary,the goal was to just do layer checks after major events that would change the map layers so scan data loading, map data loading, etc...
  useDebounce(
    () => {
      setDebouncedTriggerLayerCheck((prev) => prev + 1);
    },
    400,
    [
      selectStateAll,
      duckDBStatus,
      scanDataStatus,
      layerVisibility,
      mapboxDataEventOccurred,
    ]
  );

  // Function to check if the specified layers exist
  const checkLayers = () => {
    try {
      const newLayersExist: LayerExistenceDict = { ...layersExist };

      // Iterate through the layers and check their existence
      Object.keys(LayerEnum).forEach((key) => {
        const layerId = LayerEnum[key as keyof typeof LayerEnum];
        newLayersExist[layerId] = mapRef
          ? mapRef.getLayer(layerId) !== undefined
          : false;
      });

      // Check if there is a change in the layers' existence
      if (!_.isEqual(layersExist, newLayersExist)) {
        setLayersExist(newLayersExist);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    checkLayers();
  }, [debouncedTriggerLayerCheck, mapRef]);

  return layersExist;
};

export default useCheckLayers;
